// src/components/tickets/DevScannerSimulator.js
// Development tool that simulates scanner app check-in for testing
import React, { useState } from 'react';
import { ethers } from 'ethers';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';
const CONTRACT_ADDRESS = process.env.REACT_APP_CONTRACT_ADDRESS || process.env.REACT_APP_EVENT_TICKET_ADDRESS || '0xee8FEe7629C5829cbAC3A840F86911E5B4503501';
const CHAIN_ID = parseInt(process.env.REACT_APP_CHAIN_ID || '84532', 10);

// Only show in development
const isDev = process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEV_MODE === 'true';

export default function DevScannerSimulator({ ticket, account, eventTicket, onCheckinComplete }) {
  const [status, setStatus] = useState(null); // 'loading' | 'success' | 'error' | 'duplicate'
  const [message, setMessage] = useState('');
  const [expanded, setExpanded] = useState(false);

  // Don't render in production
  if (!isDev) {
    return null;
  }

  // Simulate the scanner flow: generate signature -> validate -> check-in
  const handleSimulatedCheckin = async () => {
    setStatus('loading');
    setMessage('Generating QR signature...');

    try {
      // Step 1: Generate signature from relayer (same as QRGenerator)
      const signatureResponse = await fetch(`${API_BASE_URL}/api/relayer/generate-signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tokenId: ticket.tokenId,
          action: 'checkin',
        }),
      });

      if (!signatureResponse.ok) {
        const errorData = await signatureResponse.json();
        throw new Error(errorData.error || 'Failed to generate signature');
      }

      const qrData = await signatureResponse.json();
      console.log('[DevScanner] QR Data generated:', qrData);

      setMessage('Verifying signature...');

      // Step 2: Verify the signature locally (same as offline-validator)
      const domain = {
        name: 'EventTicketWithEvents',
        version: '1',
        chainId: CHAIN_ID,
        verifyingContract: CONTRACT_ADDRESS.toLowerCase(),
      };

      const types = {
        TicketAuth: [
          { name: 'tokenId', type: 'uint256' },
          { name: 'action', type: 'string' },
          { name: 'expiry', type: 'uint256' },
          { name: 'nonce', type: 'uint256' },
        ],
      };

      const value = {
        tokenId: qrData.tokenId,
        action: qrData.action,
        expiry: qrData.expiry,
        nonce: qrData.nonce,
      };

      // Verify signature
      let recoveredSigner;
      try {
        recoveredSigner = ethers.verifyTypedData(domain, types, value, qrData.signature);
        console.log('[DevScanner] Signature verified, signer:', recoveredSigner);
      } catch (err) {
        throw new Error('Invalid signature: ' + err.message);
      }

      setMessage('Checking ticket ownership...');

      // Step 3: Verify ticket ownership
      if (eventTicket) {
        try {
          const balance = await eventTicket.balanceOf(account, ticket.tokenId);
          if (balance <= 0n) {
            throw new Error('You do not own this ticket');
          }
          console.log('[DevScanner] Ownership verified, balance:', balance.toString());
        } catch (err) {
          if (err.message.includes('do not own')) throw err;
          console.warn('[DevScanner] Ownership check failed:', err.message);
        }
      }

      // Step 4: Check for duplicates (using localStorage as simple storage)
      const scanKey = `dev_scan_${ticket.tokenId}_checkin`;
      const existingScan = localStorage.getItem(scanKey);

      if (existingScan) {
        const scanData = JSON.parse(existingScan);
        setStatus('duplicate');
        setMessage(`Already checked in at ${new Date(scanData.timestamp).toLocaleString()}`);
        return;
      }

      setMessage('Recording check-in...');

      // Step 5: Record the scan
      const scanRecord = {
        tokenId: ticket.tokenId,
        action: 'checkin',
        timestamp: Date.now(),
        validator: 'dev-simulator',
        account,
      };
      localStorage.setItem(scanKey, JSON.stringify(scanRecord));

      // Step 6: Try to call contract check-in if available
      if (eventTicket && typeof eventTicket.checkIn === 'function') {
        try {
          setMessage('Calling contract check-in...');
          // Note: This would need proper signer permissions in real scenario
          // For dev testing, we just simulate success
          console.log('[DevScanner] Contract check-in would be called here');
        } catch (err) {
          console.warn('[DevScanner] Contract check-in failed (expected in dev):', err.message);
        }
      }

      // Success!
      setStatus('success');
      setMessage('Check-in successful! Ticket validated.');

      // Callback
      if (onCheckinComplete) {
        onCheckinComplete({
          tokenId: ticket.tokenId,
          action: 'checkin',
          timestamp: Date.now(),
          success: true,
        });
      }

    } catch (error) {
      console.error('[DevScanner] Error:', error);
      setStatus('error');
      setMessage(error.message || 'Check-in failed');
    }
  };

  // Simulate perk redemption
  const handleSimulatedPerkRedeem = async (perkIndex) => {
    setStatus('loading');
    setMessage(`Redeeming perk ${perkIndex}...`);

    try {
      // Generate signature for perk
      const signatureResponse = await fetch(`${API_BASE_URL}/api/relayer/generate-signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tokenId: ticket.tokenId,
          action: `perk:${perkIndex}`,
        }),
      });

      if (!signatureResponse.ok) {
        const errorData = await signatureResponse.json();
        throw new Error(errorData.error || 'Failed to generate signature');
      }

      const qrData = await signatureResponse.json();
      console.log('[DevScanner] Perk QR Data:', qrData);

      // Check for duplicate perk claim
      const perkKey = `dev_scan_${ticket.tokenId}_perk_${perkIndex}`;
      const existingClaim = localStorage.getItem(perkKey);

      if (existingClaim) {
        const claimData = JSON.parse(existingClaim);
        setStatus('duplicate');
        setMessage(`Perk ${perkIndex} already claimed at ${new Date(claimData.timestamp).toLocaleString()}`);
        return;
      }

      // Record perk claim
      const claimRecord = {
        tokenId: ticket.tokenId,
        perkIndex,
        timestamp: Date.now(),
        validator: 'dev-simulator',
      };
      localStorage.setItem(perkKey, JSON.stringify(claimRecord));

      setStatus('success');
      setMessage(`Perk ${perkIndex} redeemed successfully!`);

    } catch (error) {
      console.error('[DevScanner] Perk error:', error);
      setStatus('error');
      setMessage(error.message || 'Perk redemption failed');
    }
  };

  // Clear dev scan records for this ticket
  const handleClearScans = () => {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`dev_scan_${ticket.tokenId}`)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    setStatus(null);
    setMessage('Dev scan records cleared');
    setTimeout(() => setMessage(''), 2000);
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success': return '#00e676';
      case 'error': return '#ff6b6b';
      case 'duplicate': return '#ff5252';
      case 'loading': return '#ffa726';
      default: return '#888';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success': return '✓';
      case 'error': return '✕';
      case 'duplicate': return '⟳';
      case 'loading': return '⏳';
      default: return '🔧';
    }
  };

  return (
    <div className="dev-scanner-simulator" style={{
      marginTop: '1rem',
      padding: '1rem',
      background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1), rgba(255, 87, 34, 0.1))',
      border: '2px dashed #ff9800',
      borderRadius: '8px',
    }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <h5 style={{ margin: 0, color: '#ff9800', display: 'flex', alignItems: 'center', gap: '8px' }}>
          🔧 Dev Scanner Simulator
          <span style={{ fontSize: '0.7rem', background: '#ff9800', color: '#000', padding: '2px 6px', borderRadius: '4px' }}>
            DEV ONLY
          </span>
        </h5>
        <span style={{ color: '#ff9800' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ marginTop: '1rem' }}>
          <p style={{ fontSize: '0.85rem', color: '#aaa', marginBottom: '1rem' }}>
            Simulates the staff scanner app for testing check-in and perk redemption without the mobile app.
          </p>

          {/* Status display */}
          {(status || message) && (
            <div style={{
              padding: '0.75rem',
              marginBottom: '1rem',
              borderRadius: '6px',
              background: `${getStatusColor()}22`,
              border: `1px solid ${getStatusColor()}`,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}>
              <span style={{ fontSize: '1.5rem' }}>{getStatusIcon()}</span>
              <span style={{ color: getStatusColor() }}>{message}</span>
            </div>
          )}

          {/* Main actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <button
              onClick={handleSimulatedCheckin}
              disabled={status === 'loading'}
              style={{
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #00e676, #00c853)',
                border: 'none',
                borderRadius: '8px',
                color: '#000',
                fontWeight: 'bold',
                cursor: status === 'loading' ? 'wait' : 'pointer',
                opacity: status === 'loading' ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <span>📱</span> Simulate Check-In Scan
            </button>

            {/* Perk redemption buttons */}
            {ticket.perks && ticket.perks.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem' }}>
                  Redeem Perks:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {ticket.perks.map((perkId, index) => (
                    <button
                      key={perkId}
                      onClick={() => handleSimulatedPerkRedeem(index)}
                      disabled={status === 'loading'}
                      style={{
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#fff',
                        fontSize: '0.85rem',
                        cursor: status === 'loading' ? 'wait' : 'pointer',
                      }}
                    >
                      🎁 Perk {index + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Clear button */}
            <button
              onClick={handleClearScans}
              style={{
                marginTop: '0.5rem',
                padding: '8px 16px',
                background: 'transparent',
                border: '1px solid #666',
                borderRadius: '6px',
                color: '#888',
                fontSize: '0.8rem',
                cursor: 'pointer',
              }}
            >
              🗑️ Clear Dev Scan Records
            </button>
          </div>

          <p style={{ fontSize: '0.7rem', color: '#666', marginTop: '1rem' }}>
            Token ID: {ticket.tokenId} | Contract: {CONTRACT_ADDRESS.slice(0, 10)}...
          </p>
        </div>
      )}
    </div>
  );
}
